<template>
	<Modal id="create-activity-modal">
		<template #title>{{ trans('events.activities.create') }}</template>
		<div class="grid grid-cols-1 lg:grid-cols-2 w-full gap-3">
			<LabeledObject required class="lg:col-span-2">
				<template #label>{{ trans('general.title') }}</template>
				<input v-model="form.title" type="text">
			</LabeledObject>
			<LabeledObject class="lg:col-span-2">
				<template #label>{{ trans('general.subtitle') }}</template>
				<input v-model="form.subtitle" type="text">
			</LabeledObject>
			<div class="lg:col-span-2 flex items-end gap-3">
				<LabeledObject full>
					<template #label>{{ trans('events.activities.maximum') }}</template>
					<IconNumberInput v-model="form.maximum" :min="0" :step="1" icon-class="fa-solid fa-ticket" full/>
				</LabeledObject>
				<Switch v-model.number="form.active" :label="trans('events.activities.active')"/>
				<Switch v-model.number="form.mandatory" :label="trans('events.activities.mandatory')"/>
			</div>
			<LabeledObject required>
				<template #label>{{ trans('events.activities.begins_at') }}</template>
				<IconDateTimeInput v-model="form.begins_at" icon-class="fa-solid fa-hourglass-start" full/>
			</LabeledObject>
			<LabeledObject required>
				<template #label>{{ trans('events.activities.ends_at') }}</template>
				<IconDateTimeInput v-model="form.ends_at" icon-class="fa-solid fa-hourglass-end" full/>
			</LabeledObject>
		</div>
		<template #footer>
			<PrimaryButton :color="Color.Green" @click.stop="create">
				<i class="fa-solid fa-check mr-2"/>
				{{ trans('general.confirm') }}
			</PrimaryButton>
		</template>
	</Modal>
	<div class="p-8 rounded-lg border-2 border-dashed border-gray-400 hover:border-green-400 dark:hover:border-green-600 text-gray-400 hover:text-green-400 dark:hover:text-green-600 hover:bg-green-50/30 dark:hover:bg-green-950/30 flex flex-col items-center justify-center cursor-pointer gap-2" @click.stop="showModal('create-activity-modal')">
		<i class="fa-solid fa-plus text-2xl"/>
		<p>{{ trans('events.activities.create') }}</p>
	</div>
</template>
<script setup lang="ts">
import {route} from 'ziggy-js'
import {trans} from 'laravel-vue-i18n'
import {Color} from '@/Types/Enums/Color'
import {ref, type Ref, defineProps} from 'vue'
import type {Event} from '@/Types/Events/Event'
import {type Pack} from '@/Types/Events/Packs/Pack'
import {InertiaForm, useForm} from '@inertiajs/vue3'
import Modal from '@/Components/UI/Modals/Modal.vue'
import Switch from '@/Components/UI/Switches/Switch.vue'
import LabeledObject from '@/Components/UI/LabeledObject.vue'
import PrimaryButton from '@/Components/UI/Buttons/PrimaryButton.vue'
import type {Activity} from '@/Types/Events/Packs/Activities/Activity'
import IconNumberInput from '@/Components/UI/Inputs/IconNumberInput.vue'
import IconDateTimeInput from '@/Components/UI/Inputs/IconDateTimeInput.vue'
import {showInfo, showModal, showSuccess, showValidationErrors} from '@/Helpers/general'

const props = defineProps<{
	pack:Pack,
	event:Event,
}>()

const pack:Ref<Pack> = ref(props.pack)
const event:Ref<Event> = ref(props.event)
const working:Ref<boolean> = ref(false)
const form:InertiaForm<Activity> = useForm<Activity>({
	title: '',
	subtitle: '',
	begins_at: '',
	ends_at: '',
	maximum: 0,
	active: false,
} as Activity)

function create():void{
	if(!working.value){
		working.value = true
		showInfo(trans('general.inserting'))
		form.post(route('events.packs.activities.store', {
			event: event.value.id,
			pack: pack.value.id,
		}), {
			onSuccess: () => {
				showSuccess(trans('general.inserted'))
			},
			onError: showValidationErrors,
			onFinish: () => {
				working.value = false
			}
		})
	}
}
</script>